import Heading from "@ui-kit/typography/Heading/Heading.web";
import Button from "@ui-kit/form/Button/Button.web";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import ReadMore from "@ui-kit/typography/ReadMore/ReadMore.web";
import AllInPricing from "@shared/modules/financials/components/AllInPricing/AllInPricing.web";
import Card from "@ui-kit/data-display/Card/Card.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import { getItemPrice } from "@shared/modules/financials/utils/price-utils";
import { useTranslation } from "@shared/utils/internationalization";
import { useState } from "react";
import Tooltip from "@ui-kit/overlays/Tooltip/Tooltip.web";
import { SeatSelectModalProps, SeatType } from "./SeatSelectModal.props";
import { useSeatSelectModal } from "./useSeatSelectModal.web";
import SeatPermissionSelect from "./SeatPermissionSelect/SeatPermissionSelect.web";
import { SeatActionRequiredAlert } from "../SeatActionRequiredAlert/SeatActionRequiredAlert.web";
import { SeatingModal } from "../../enums/seating-enums";

const SeatSelectModal = (props: SeatSelectModalProps) => {
  const { venueData, seatsToAdd, isOpen, bypassFullPurchase, hidePrice } =
    props;
  const { originalSeat, modalType } = seatsToAdd;
  const { t } = useTranslation("seating");

  const [seats, setSeats] = useState<Array<SeatType>>([]);
  const updateSeatArray = (inputSeats: Array<SeatType>) => {
    setSeats(inputSeats);
  };

  const {
    isIsolatedSelectionDisabled,
    paymentType,
    purchaseSourcePlatform,
    hideDetailCard,
    allSeatsSelected,
    cancelModal,
    confirmSelection,
    confirmFullPurchase,
    getDescriptionText,
  } = useSeatSelectModal({ ...props, seats, updateSeatArray });

  return (
    <Modal
      isOpen={isOpen}
      onClose={cancelModal}
      isCentered
      size="3xl"
      scrollBehavior="inside"
    >
      <Modal.Header>
        {t("Section {{section}}, Row {{row}}, Seat {{seat}}", {
          section: originalSeat.segment_identifier,
          row: originalSeat.location_identifier,
          seat: originalSeat.identifier,
        })}
      </Modal.Header>
      <Modal.Body>
        <Flex
          data-testid="seat-selection-modal"
          w="full"
          direction="column"
          gap="md"
        >
          {/* Displays an explanation to the user (includes header text in alert title) */}
          <SeatActionRequiredAlert {...getDescriptionText()} />
          {seats?.map((seat) => (
            <Card
              key={seat?.$data?.id}
              hidden={hideDetailCard}
              w="full"
              border="1px solid"
              borderColor="gray.400"
              rounded="sm"
            >
              <Text color="font.dark">
                <span>{t("Section")} </span>
                <strong>{seat?.$data?.segment_identifier}</strong>
                <span>, {t("Row")} </span>
                <strong>{seat?.$data?.location_identifier}</strong>
                <span>, {t("Seat")} </span>
                <strong>{seat?.$data?.identifier}</strong>
              </Text>
              {/* Displays a item type selector for isolated seats with more than 1 item type */}
              {modalType === SeatingModal.Isolated &&
                seat.permissions &&
                seat.permissions?.length > 1 && (
                  <SeatPermissionSelect
                    currentSeat={seat}
                    seatArray={seats}
                    updateSeats={updateSeatArray}
                  />
                )}
              <Flex direction="column" hidden={!seat.selectedPermission}>
                <Flex width="100%" maxW="100%" alignItems="center">
                  {/* ColorIndicator */}
                  <Text
                    marginTop="2xs"
                    width="element.sm"
                    height="element.sm"
                    borderRadius="50%"
                    marginRight="xs"
                    style={{
                      background:
                        seat.selectedPermission?.seatingItemType?.colorFill,
                      position: "relative",
                    }}
                  />
                  <Heading size="2xl">
                    {seat.selectedPermission?.seatingItemType?.name}
                  </Heading>
                </Flex>

                {!hidePrice && (
                  <Heading mt="md" as="h3" size="lg">
                    <CurrencyFormat
                      value={getItemPrice(
                        seat?.selectedPermission?.seatingItemType?.price?.toString(),
                        Boolean(venueData.all_in_pricing_is_enabled),
                        seat?.selectedPermission?.seatingItemType
                          ?.fees_pricing_info,
                        paymentType,
                        purchaseSourcePlatform,
                        undefined,
                        seat.selectedPermission?.seatingItemType
                          ?.pay_what_you_can_configuration
                      )}
                      displayType="text"
                      currency={venueData.currency}
                      includeSuffix
                    />
                  </Heading>
                )}
                {!hidePrice && (
                  <AllInPricing
                    hasAllInPricing={Boolean(
                      venueData.all_in_pricing_is_enabled
                    )}
                    feesPricingInfo={
                      seat.selectedPermission?.seatingItemType
                        ?.fees_pricing_info
                    }
                    price={seat.selectedPermission?.seatingItemType?.price?.toString()}
                    currency={venueData.currency}
                    purchaseSourcePlatform={purchaseSourcePlatform}
                    paymentType={paymentType}
                  />
                )}
                {seat.selectedPermission?.seatingItemType?.description && (
                  <ReadMore
                    descriptionHtml={
                      seat.selectedPermission.seatingItemType?.description
                    }
                    openReadMoreText={t("Read More", { ns: "common" })}
                    closeReadMoreText={t("Show Less", { ns: "common" })}
                  />
                )}
              </Flex>
            </Card>
          ))}
        </Flex>
      </Modal.Body>
      <Modal.Footer
        borderTop="1px solid"
        borderColor="gray.400"
        flexDirection="row"
      >
        <Flex justifyContent="space-between">
          <Button.Ghost
            data-testid="seat-select-modal-cancel"
            onClick={cancelModal}
          >
            {t("Cancel", { ns: "common" })}
          </Button.Ghost>
        </Flex>

        <Flex gap={5}>
          {bypassFullPurchase && confirmFullPurchase && (
            <Tooltip
              placement="top"
              hasArrow
              label={t(
                "Bypass full table restriction and add 'Sec:{{section}}' Row: {{row}} Seat: {{seat}}",
                {
                  section: originalSeat.segment_identifier,
                  row: originalSeat.location_identifier,
                  seat: originalSeat.identifier,
                }
              )}
            >
              <Button
                data-testid="seat-select-modal-bypass"
                minWidth="element.7xl"
                colorPalette="primary"
                variant="solid"
                onClick={confirmFullPurchase}
              >
                {t("Bypass")}
              </Button>
            </Tooltip>
          )}

          <Button
            data-testid="seat-select-modal-continue"
            minWidth="element.7xl"
            colorPalette="primary"
            variant="solid"
            disabled={!allSeatsSelected}
            onClick={confirmSelection}
          >
            {isIsolatedSelectionDisabled
              ? t("Select new seat")
              : t("Continue", { ns: "common" })}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};

export default SeatSelectModal;
